import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Collapse, TableCell, TableRow } from '@mui/material';

import {
    loadContractDetailsForContractId,
    loadServiceDetailsForContractId
} from '../../../store/directusService';
import { loadParticipantByPtCode } from '../../../store/participantService';
import { reverseFormatDate } from '../../../utils/dateFunctions';
import RadioButtons from '../../formElements/RadioButtons';

import CannotClaimDetails from './CannotClaimDetails';
import ReversionDetails from './ReversionDetails';

import table from '../../../commonStyles/tables.module.css';

const expandedCellStyles = {
    paddingBottom: 0,
    paddingTop: 0,
    paddingLeft: 0,
    paddingRight: 0,
    borderTop: 'none',
    borderBottom: 'none'
};

const ClaimTableRow = ({ row, isDisabled, toggleDropdown, openId, onRowChange }) => {
    // HOOKS
    const dispatch = useDispatch();
    const navigate = useNavigate();

    // LOCAL STATE
    const [open, setOpen] = useState(null);
    const [isPTRequestSent, setIsPTRequestSent] = useState(false);

    // STORE STATE
    const currentParticipant = useSelector(
        (state) => state.entities.participantService.currentParticipant
    );
    const contractDetailsById = useSelector(
        (state) => state.entities.directusService.contractDetailsById
    );
    const servicesByContractId = useSelector(
        (state) => state.entities.directusService.servicesByContractId
    );

    // USE EFFECTS
    useEffect(() => {
        if (openId !== row.id) setOpen(null);
    }, [openId]);

    useEffect(() => {
        if (isPTRequestSent && currentParticipant.ptCode === row.ptCode) {
            if (!(currentParticipant.contractId in contractDetailsById))
                dispatch(loadContractDetailsForContractId(currentParticipant.contractId));
            if (!(currentParticipant.contractId in servicesByContractId))
                dispatch(loadServiceDetailsForContractId(currentParticipant.contractId));
        }
    }, [isPTRequestSent, currentParticipant]);

    useEffect(() => {
        if (
            isPTRequestSent &&
            currentParticipant?.ptCode === row.ptCode &&
            contractDetailsById[currentParticipant.contractId] &&
            servicesByContractId[currentParticipant.contractId]
        )
            navigate('/edit_participant');
    }, [isPTRequestSent, contractDetailsById, servicesByContractId, currentParticipant]);

    // EVENT HANDLERS
    const onClickPTCode = () => {
        if (!row?.ptCode) return;
        dispatch(loadParticipantByPtCode(row.ptCode));
        setIsPTRequestSent(true);
    };

    const onStatusChange = (selection) => {
        const updatedRow = { ...row, selection };
        onRowChange(updatedRow);
    };

    const onToggleDropdown = () => {
        toggleDropdown(row.id);
        setOpen(open === row.id ? null : row.id);
    };

    // RENDER
    return (
        <>
            <TableRow
                key={row.id}
                hover
                className={`${table.tableRowStripes} ${table.oldLace} ${table.canOpen}`}>
                <TableCell className={table.rowLink} onClick={onClickPTCode} disabled={isDisabled}>
                    {row?.ptCode}
                </TableCell>
                <TableCell onClick={onToggleDropdown}>{row?.poNumber}</TableCell>
                <TableCell onClick={onToggleDropdown}>
                    {row?.eventTypeDisplayName ? row.eventTypeDisplayName : row.eventType}
                </TableCell>
                <TableCell onClick={onToggleDropdown}>
                    {reverseFormatDate(row?.eventDate)}
                </TableCell>
                <TableCell onClick={onToggleDropdown}>
                    {reverseFormatDate(row?.lastStatusUpdated)}
                </TableCell>
                <TableCell onClick={onToggleDropdown}>{row.statusName}</TableCell>
                <TableCell>
                    <RadioButtons
                        id={`statusUpdate-${row.id}`}
                        radioButtons={[
                            {
                                id: 'proceed',
                                label: '',
                                onChangeVal: 'Proceed',
                                buttonDisabled: row.statusName === 'Paid'
                            },
                            { id: 'revert', onChangeVal: 'Revert' },
                            { id: 'cannotClaim', onChangeVal: 'CannotClaim' },
                            { id: 'skip', onChangeVal: 'Skip' }
                        ]}
                        customClass="tableRowRadioWrapper"
                        value={row.selection}
                        onChange={(value) => onStatusChange(value)}
                    />
                </TableCell>
            </TableRow>
            {row?.selection !== 'Revert' && row?.selection !== 'CannotClaim' ? (
                ''
            ) : (
                <TableRow>
                    <TableCell style={expandedCellStyles} colSpan={7}>
                        <Collapse in={open === row.id} timeout="auto" unmountOnExit>
                            {row?.selection === 'Revert' ? (
                                <ReversionDetails row={row} onClose={() => setOpen(null)} />
                            ) : (
                                <CannotClaimDetails row={row} onClose={() => setOpen(null)} />
                            )}
                        </Collapse>
                    </TableCell>
                </TableRow>
            )}
        </>
    );
};

ClaimTableRow.propTypes = {
    row: PropTypes.object,
    isDisabled: PropTypes.bool,
    openId: PropTypes.string,
    toggleDropdown: PropTypes.func,
    onRowChange: PropTypes.func
};

export default ClaimTableRow;
